// src/contexts/ItineraryContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';

// Types
export interface Trip {
  id: string;
  title: string;
  description?: string;
  destination: string;
  startDate: Date;
  endDate: Date;
  purpose?: string;
  style?: string;
  pace?: string;
  budget?: number[];
  groupSize?: number;
  groupRelationship?: string;
  interests?: string[];
  specialOccasion?: string;
}

export interface Activity {
  id: string;
  title: string;
  description: string;
  time?: string;
  location: string;
  type: 'activity' | 'food' | 'accommodation' | 'transportation';
  cost?: number;
  duration?: string;
  imageUrl?: string;
}

export interface TimeSlot {
  id: string;
  time: string;
  dayIndex: number;
  activityId: string | null;
}

export interface ItineraryDay {
  date: Date;
  slots: TimeSlot[];
}

interface ItineraryContextType {
  trips: Trip[];
  selectedTrip: Trip | null;
  itinerary: ItineraryDay[];
  activities: Activity[];
  loading: boolean;
  error: string | null;
  setSelectedTrip: (trip: Trip | null) => void;
  setActivities: (activities: Activity[]) => void;
  setItinerary: (itinerary: ItineraryDay[]) => void;
  saveItinerary: (itinerary: ItineraryDay[]) => Promise<boolean>;
  fetchTripById: (tripId: string) => Promise<Trip | null>;
  generateActivities: (destination: string) => Promise<Activity[]>;
  addToItinerary: (activityId: string, tripId: string, dayIndex: number, timeSlotIndex: number) => Promise<void>;
}

const ItineraryContext = createContext<ItineraryContextType | undefined>(undefined);

export const useItinerary = () => {
  const context = useContext(ItineraryContext);
  if (!context) {
    throw new Error('useItinerary must be used within an ItineraryProvider');
  }
  return context;
};

export const ItineraryProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [trips, setTrips] = useState<Trip[]>([]);
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null);
  const [itinerary, setItinerary] = useState<ItineraryDay[]>([]);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch all trips
  useEffect(() => {
    const fetchTrips = async () => {
      try {
        setLoading(true);
        const tripsCollection = collection(db, 'trips');
        const tripSnapshot = await getDocs(tripsCollection);
        
        const tripData: Trip[] = [];
        
        tripSnapshot.forEach((doc) => {
          const data = doc.data();
          
          // Extract destination from title if missing
          const destination = data.destination || data.title?.split('-')[0]?.trim() || 'Unknown Location';
          
          tripData.push({
            id: doc.id,
            title: data.title,
            description: data.description || '',
            destination: destination, // Ensure destination always has a value
            startDate: data.startDate.toDate(),
            endDate: data.endDate.toDate(),
            purpose: data.purpose || '',
            style: data.style || '',
            pace: data.pace || '',
            budget: data.budget || [100, 500],
            groupSize: data.groupSize || 2,
            groupRelationship: data.groupRelationship || '',
            interests: data.interests || [],
            specialOccasion: data.specialOccasion || ''
          });
        });
        
        setTrips(tripData.sort((a, b) => b.startDate.getTime() - a.startDate.getTime()));
        setLoading(false);
      } catch (err) {
        console.error('Error fetching trips:', err);
        setError('Failed to load trips. Please try again.');
        setLoading(false);
      }
    };
    
    fetchTrips();
  }, []);

  // Fetch trip by ID
  const fetchTripById = async (tripId: string): Promise<Trip | null> => {
    try {
      setLoading(true);
      const tripDoc = await getDoc(doc(db, 'trips', tripId));
      
      if (tripDoc.exists()) {
        const data = tripDoc.data();
        
        // Extract destination from title if missing
        const destination = data.destination || data.title?.split('-')[0]?.trim() || 'Unknown Location';
        
        const trip: Trip = {
          id: tripDoc.id,
          title: data.title,
          description: data.description || '',
          destination: destination, // Ensure destination always has a value
          startDate: data.startDate.toDate(),
          endDate: data.endDate.toDate(),
          purpose: data.purpose || '',
          style: data.style || '',
          pace: data.pace || '',
          budget: data.budget || [100, 500],
          groupSize: data.groupSize || 2,
          groupRelationship: data.groupRelationship || '',
          interests: data.interests || [],
          specialOccasion: data.specialOccasion || ''
        };
        
        setSelectedTrip(trip);
        setLoading(false);
        return trip;
      } else {
        setError('Trip not found');
        setLoading(false);
        return null;
      }
    } catch (err) {
      console.error('Error fetching trip:', err);
      setError('Failed to load trip. Please try again.');
      setLoading(false);
      return null;
    }
  };

  // Generate activities based on destination
  const generateActivities = async (destination: string): Promise<Activity[]> => {
    try {
      setLoading(true);
      
      // This is a mock implementation - in a real app, you'd call an API
      // or use AI service to generate activities based on the destination
      const mockActivities = getMockActivities(destination);
      
      setActivities(mockActivities);
      setLoading(false);
      return mockActivities;
    } catch (err) {
      console.error('Error generating activities:', err);
      setError('Failed to generate activities. Please try again.');
      setLoading(false);
      return [];
    }
  };

  // Mock function to generate activities based on destination
  const getMockActivities = (destination: string): Activity[] => {
    const lowerDestination = destination.toLowerCase();
    
    // Add more destinations with activities as needed
    if (lowerDestination.includes('paris')) {
      return [
        {
          id: 'a1',
          title: 'Eiffel Tower Visit',
          description: 'Experience the iconic symbol of Paris with spectacular views of the city.',
          location: 'Champ de Mars, 5 Avenue Anatole France',
          type: 'activity',
          cost: 25,
          duration: '2 hours',
          imageUrl: 'https://source.unsplash.com/random/?eiffel+tower'
        },
        {
          id: 'a2',
          title: 'Louvre Museum',
          description: 'Explore the world\'s largest art museum and see masterpieces like the Mona Lisa.',
          location: 'Rue de Rivoli, 75001',
          type: 'activity',
          cost: 15,
          duration: '3 hours',
          imageUrl: 'https://source.unsplash.com/random/?louvre'
        },
        {
          id: 'a3',
          title: 'Café de Flore',
          description: 'Historic café known for famous patrons including Hemingway and Picasso.',
          location: '172 Blvd Saint-Germain',
          type: 'food',
          cost: 30,
          duration: '1.5 hours',
          imageUrl: 'https://source.unsplash.com/random/?cafe+paris'
        }
      ];
    } else if (lowerDestination.includes('tokyo')) {
      return [
        {
          id: 'a4',
          title: 'Shinjuku Gyoen National Garden',
          description: 'Beautiful garden with traditional Japanese landscaping and cherry trees.',
          location: 'Shinjuku, Tokyo',
          type: 'activity',
          cost: 5,
          duration: '2 hours',
          imageUrl: 'https://source.unsplash.com/random/?shinjuku+garden'
        },
        {
          id: 'a5',
          title: 'Tsukiji Outer Market',
          description: 'Explore the famous food market and try fresh seafood.',
          location: 'Tsukiji, Tokyo',
          type: 'food',
          cost: 20,
          duration: '2 hours',
          imageUrl: 'https://source.unsplash.com/random/?tsukiji+market'
        }
      ];
    }
    
    // Default activities for any destination
    return [
      {
        id: 'a6',
        title: 'City Tour',
        description: 'Explore the main attractions of the city with a guided tour.',
        location: 'City Center',
        type: 'activity',
        cost: 20,
        duration: '3 hours',
        imageUrl: 'https://source.unsplash.com/random/?city+tour'
      },
      {
        id: 'a7',
        title: 'Local Restaurant',
        description: 'Try authentic local cuisine at a popular restaurant.',
        location: 'Downtown Area',
        type: 'food',
        cost: 25,
        duration: '1.5 hours',
        imageUrl: 'https://source.unsplash.com/random/?restaurant'
      },
      {
        id: 'a8',
        title: 'Luxury Hotel',
        description: 'Top-rated accommodation in the heart of the city.',
        location: 'Main Boulevard',
        type: 'accommodation',
        cost: 180,
        imageUrl: 'https://source.unsplash.com/random/?luxury+hotel'
      }
    ];
  };

  // Save itinerary to database (mock implementation)
  const saveItinerary = async (itinerary: ItineraryDay[]): Promise<boolean> => {
    try {
      // In a real app, you'd save to Firestore here

      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      return true;
    } catch (err) {
      console.error('Error saving itinerary:', err);
      setError('Failed to save itinerary. Please try again.');
      return false;
    }
  };

  const addToItinerary = async (activityId: string, tripId: string, dayIndex: number, timeSlotIndex: number): Promise<void> => {
    if (!itinerary[dayIndex] || !itinerary[dayIndex].slots[timeSlotIndex]) {
      throw new Error('Invalid day or time slot');
    }
    
    const updatedItinerary = [...itinerary];
    updatedItinerary[dayIndex].slots[timeSlotIndex].activityId = activityId;
    setItinerary(updatedItinerary);
  };

  const value = {
    trips,
    selectedTrip,
    itinerary,
    activities,
    loading,
    error,
    setSelectedTrip,
    setItinerary,
    setActivities,
    fetchTripById,
    generateActivities,
    saveItinerary,
    addToItinerary
  };

  return (
    <ItineraryContext.Provider value={value}>
      {children}
    </ItineraryContext.Provider>
  );
};

export default ItineraryContext;